import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Coins, Loader2, MapPin, Search, Trophy, Users, X } from "lucide-react";
import LeagueDetail from "@/components/LeagueDetail";

export default function UpcomingLeagues({ refreshKey }) {
  const [leagues, setLeagues] = useState([]);
  const [reg, setReg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/leagues").then(({ data }) => setLeagues(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    params.delete("session_id");
    setParams(params, { replace: true });
    const poll = async (attempt = 0) => {
      if (attempt >= 6) return toast.error("Payment status check timed out. Check back shortly.");
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("You're registered! Pending roster approval."); load(); return; }
        if (data.status === "expired") return toast.error("Payment session expired.");
        setTimeout(() => poll(attempt + 1), 2000);
      } catch { toast.error("Could not verify payment."); }
    };
    poll();
  }, []); // eslint-disable-line

  const cancel = async (lg) => {
    try { await api.delete(`/admin/leagues/${lg.id}`); toast.success(`${lg.name} cancelled`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (leagues.length === 0) return null;
  return (
    <div className="mb-8" data-testid="upcoming-leagues">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-pb-cyan" />
        <h3 className="font-display font-bold text-xl uppercase">Upcoming Leagues</h3>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {leagues.map((lg) => (
          <div key={lg.id} className="bg-pb-surface border border-pb-border hover:border-pb-cyan/50 transition-colors" data-testid={`league-card-${lg.id}`}>
            {lg.image && <img src={lg.image} alt={lg.name} className="w-full h-28 object-cover border-b border-pb-border" />}
            <div className="p-4">
              <button data-testid={`league-open-${lg.id}`} onClick={() => setDetail(lg)}
                className="font-display font-bold uppercase leading-tight text-left hover:text-pb-cyan transition-colors">{lg.name}</button>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 space-y-0.5">
                <div className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Starts {lg.start_date} · {lg.weeks} weeks{lg.play_day ? ` · ${lg.play_day}${lg.play_time ? ` ${lg.play_time}` : ""}` : ""}</div>
                {(lg.facility || lg.city) && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {[lg.facility, lg.city, lg.state].filter(Boolean).join(", ")} · {lg.environment}</div>}
                <div className="flex items-center gap-1"><Users className="w-3 h-3" /> {lg.registered} team{lg.registered === 1 ? "" : "s"} in{lg.spots_left !== null ? ` · ${lg.spots_left} spots left` : ""}</div>
              </div>
              {lg.description && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{lg.description}</p>}
              <div className="flex items-center justify-between mt-3">
                <span className="font-display font-black text-pb-cyan">{lg.fee > 0 ? `$${lg.fee} / team` : "FREE"}</span>
                <div className="flex items-center gap-2">
                  {lg.can_manage && (
                    <button data-testid={`league-cancel-${lg.id}`} onClick={() => cancel(lg)}
                      className="p-1.5 border border-pb-border text-zinc-500 hover:border-red-500 hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  )}
                  {lg.my_registration ? (
                    <span className="text-[9px] font-black uppercase tracking-widest border border-emerald-500 text-emerald-400 px-2 py-1.5">Registered</span>
                  ) : lg.spots_left === 0 ? (
                    <span className="text-[9px] font-black uppercase tracking-widest border border-zinc-700 text-zinc-500 px-2 py-1.5">Full</span>
                  ) : (
                    <button data-testid={`league-register-${lg.id}`} onClick={() => setReg(lg)}
                      className="bg-pb-cyan text-black font-black uppercase tracking-widest px-3 py-1.5 text-[10px] hover:opacity-90 active:scale-95 transition-all">Register</button>
                  )}
                  <button data-testid={`league-view-${lg.id}`} onClick={() => setDetail(lg)}
                    className="border border-pb-border text-zinc-400 font-black uppercase tracking-widest px-3 py-1.5 text-[10px] hover:border-pb-cyan hover:text-pb-cyan transition-colors">View</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {reg && <RegisterModal league={reg} onClose={() => setReg(null)} onDone={() => { setReg(null); load(); }} />}
      {detail && <LeagueDetail league={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function RegisterModal({ league, onClose, onDone }) {
  const [teamName, setTeamName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [partner, setPartner] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/members/search", { params: { q: query } }); setResults(data); } catch { /* noop */ }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const go = async (payBullion) => {
    if (!teamName.trim()) return toast.error("Team name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/bpl/register/checkout", {
        league_id: league.id, team_name: teamName, partner_id: partner?.id || null,
        origin_url: window.location.origin, pay_with_bullion: payBullion,
      });
      if (data.url) { window.location.href = data.url; return; }
      toast.success(data.free ? "You're registered — pending roster approval!" : "Paid with BULLION — you're registered!");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="league-register-modal">
      <div className="bg-pb-surface border border-pb-cyan/40 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-black uppercase text-lg">Register — {league.name}</div>
          <button data-testid="lrm-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-zinc-500 mb-4">Coed league · starts {league.start_date} · {league.fee > 0 ? `$${league.fee} per team` : "free entry"}</div>
        <label className="block text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Team Name *</label>
        <input data-testid="lrm-team-name" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="The Dink Dynasty"
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan text-sm mb-3" />
        <label className="block text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Partner (optional)</label>
        {partner ? (
          <div className="flex items-center justify-between border border-pb-cyan/40 bg-pb-cyan/5 px-3 py-2 text-sm mb-3" data-testid="lrm-partner-selected">
            <span className="font-bold">{partner.name}</span>
            <button onClick={() => setPartner(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="relative mb-3">
            <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="lrm-partner-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search members…"
              className="w-full bg-pb-elevated border border-pb-border pl-9 pr-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan text-sm" />
            {results.length > 0 && (
              <div className="absolute z-10 left-0 right-0 top-full bg-pb-elevated border border-pb-border max-h-40 overflow-y-auto">
                {results.map((r) => (
                  <button key={r.id} data-testid={`lrm-partner-${r.id}`} onClick={() => { setPartner(r); setQuery(""); setResults([]); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-pb-surface">{r.name}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {partner && league.fee > 0 && (
          <p className="text-[11px] text-amber-400/90 mb-1" data-testid="lrm-partner-pay-note">
            {partner.name} pays their own ${league.fee} entry — they'll get a notification + email with a payment link
            and show as PENDING until they pay.
          </p>
        )}
        <div className="flex flex-col gap-2 mt-4">
          <button data-testid="lrm-pay-card" onClick={() => go(false)} disabled={busy}
            className="w-full bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-3 text-xs hover:opacity-90 active:scale-95 transition-all disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : league.fee > 0 ? `Pay $${league.fee} & Register` : "Register Free"}
          </button>
          {league.fee > 0 && (
            <button data-testid="lrm-pay-bullion" onClick={() => go(true)} disabled={busy}
              className="w-full flex items-center justify-center gap-2 border border-yellow-500/50 text-yellow-300 font-black uppercase tracking-widest px-4 py-3 text-xs hover:bg-yellow-500/10 transition-colors disabled:opacity-40">
              <Coins className="w-3.5 h-3.5" /> Pay with BULLION Coin
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
